<svelte:options runes={true} />

<script lang="ts">
	import { Heading, Input, Label, Sidebar, SidebarGroup, SidebarItem, SidebarWrapper, Spinner, Tooltip } from "flowbite-svelte";
	import { getContext, tick, type ComponentEvents } from "svelte";
	import { createEventDispatcher } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { Point } from "#gen/bundles/v1/common_pb.js";
	import { DataType } from "#gen/common/v1/types_pb.js";
	import type { StaticAsset } from "#gen/staticassets/v1/static_assets_pb.js";
	import type { StaticAssetClient } from "#lib/api/static-assets.js";
	import Dropzone from "#lib/components/Dropzone.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import Modal from "#lib/components/Modal.svelte";
	import P from "#lib/components/P.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import ImagePicker from "#lib/components/images/ImagePicker.svelte";
	import { UPLOADABLE_IMAGE_FILE_TYPES } from "#lib/components/images/constants.js";
	import FileExplorer from "#lib/components/images/file-management/FileExplorer.svelte";
	import { getContentType, isDataProperty, validateUploadFile } from "#lib/components/images/utils.js";
	import { WorkspacesActions } from "#lib/permissions/actions.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Readable, Writable } from "svelte/store";

	interface Props {
		open: boolean;
		value?: string | undefined;
		focalPoint?: Point | undefined;
		aspectRatio?: number | undefined;
		initialAspectRatio?: number | undefined;
		caption?: string | undefined;
		shape?: "circle" | "rectangle" | undefined;
		finalWidth?: number | undefined;
		finalHeight?: number | undefined;
		imageTypeCaption?: string;
		fileTypes?: string[];
		contentType?: string | null | undefined;
		allowFocalPointSelection?: boolean;
		fromOrgAdmin?: boolean;
		showAspectRatioPicker?: boolean;
		preferFreeformAspectRatio?: boolean;
	}

	let {
		open = $bindable(),
		value,
		aspectRatio = undefined,
		initialAspectRatio = undefined,
		caption = undefined,
		shape = undefined,
		finalWidth = undefined,
		finalHeight = undefined,
		imageTypeCaption = "image",
		fileTypes = UPLOADABLE_IMAGE_FILE_TYPES,
		contentType = undefined,
		focalPoint = undefined,
		allowFocalPointSelection = false,
		fromOrgAdmin = false,
		showAspectRatioPicker = false,
		preferFreeformAspectRatio = false,
	}: Props = $props();

	const staticAssetClient: StaticAssetClient = getContext("staticAssetClient");
	const provider: Writable<TokenProvider> = getContext("tokenProvider");
	const permissions: Readable<Set<string>> = getContext("permissions");
	const tabs = ["Select from library", "Point to web image"];

	let disableUploadNew = $derived(!fromOrgAdmin && !$permissions.has(WorkspacesActions.CREATE_STATIC_ASSET));

	const dispatch = createEventDispatcher();

	let tab = $state(0);
	let isFetching = $state(false);
	let loading = $state(false);
	let changedValue = $state(value);
	let openCropper = $state(false);
	let selectedImgFromLibrary: StaticAsset | undefined = $state(undefined);
	let hasSelectedFromLibrary = $derived(tab === 0 && Boolean(selectedImgFromLibrary));
	let uploadId = $state("");
	let imageSize = $state(0);
	let fileName = $state("");
	let modalError = $state<unknown>(undefined);
	let showFileManagerCheckbox = $derived(!(hasSelectedFromLibrary || (value && value === changedValue)) && $permissions.has(WorkspacesActions.CREATE_STATIC_ASSET));

	$effect(() => {
		const isTokenized = Boolean(value && (isDataProperty(value) || $provider?.hasSubstitutionToken(value)));
		if (!isTokenized) {
			tick().then(() => {
				if (value) {
					switchToCropper(value);
				}
			});
		} else if (isTokenized) {
			tab = 1;
		}
	});

	async function switchToCropper(url: string) {
		openCropper = true;
		contentType = await getContentType(url);
	}

	function handleSelectTab(e: MouseEvent, selectedTab: number) {
		e.preventDefault();
		tab = selectedTab;
	}

	function handleClose() {
		modalError = undefined;
		open = false;
	}

	async function onFileDropped(e: ComponentEvents<Dropzone>["change"]) {
		loading = true;
		modalError = undefined;
		try {
			imageSize = (await validateUploadFile(e.detail)).imageSize;
			const urlObj = await staticAssetClient.upload(e.detail);
			selectedImgFromLibrary = undefined;
			value = urlObj.url;
			uploadId = urlObj.id;
			contentType = e.detail.type;
			openCropper = true;
			fileName = e.detail.name;
		} catch (e) {
			modalError = e;
		} finally {
			loading = false;
		}
	}

	function handleTokenizedChange(e: CustomEvent<string | undefined>) {
		changedValue = e.detail ?? "";
	}

	function handleChange(e: Event) {
		const target = e.target as HTMLInputElement;
		changedValue = target.value ?? "";
	}

	async function onContinue() {
		const nextValue = (changedValue ?? "").trim();

		// Persist data properties immediately — they can't be loaded into the cropper.
		if (!hasSelectedFromLibrary && (!nextValue || isDataProperty(nextValue) || $provider?.hasSubstitutionToken(nextValue))) {
			dispatch("change", { value: nextValue, contentType: null });
			handleClose();
			return;
		}

		isFetching = true;
		modalError = undefined;
		try {
			if (hasSelectedFromLibrary) {
				value = selectedImgFromLibrary?.uploadUrl;
				fileName = selectedImgFromLibrary?.name ?? "";
			} else {
				value = nextValue;
				contentType = await getContentType(nextValue);
			}
			openCropper = true;
		} catch (e) {
			modalError = e;
		} finally {
			isFetching = false;
		}
	}

	function replace() {
		openCropper = false;
	}
</script>

{#if !openCropper}
	<Modal title="Choose an image" class="max-h-173 max-w-221.5" bodyClass={twMerge("p-4 md:p-5 space-y-4 flex-1 overscroll-contain", tab === 1 ? "overflow-y-visible" : "overflow-y-auto")} style="height: 95vh;" size="xl" bind:open on:close={handleClose} bind:lastError={modalError}>
		<div class="flex h-full gap-8">
			<Sidebar class="h-full w-43.75 flex-none overflow-visible">
				<SidebarWrapper class="h-full w-full overflow-visible bg-white px-1">
					<SidebarGroup class="w-full">
						{#each tabs as title, idx (title)}
							{@const isDisabled = disableUploadNew && idx === 1}
							<div id={`sidebar-item-${idx}`}>
								<SidebarItem label={title} class={twMerge(tab === idx ? "w-full bg-gray-100 dark:bg-gray-600" : "w-full", isDisabled && "pointer-events-none opacity-50")} spanClass="grow text-md font-medium" on:click={e => !isDisabled && handleSelectTab(e, idx)} data-action-name="file-manager-sidebar-tab" data-action-title={title}></SidebarItem>
							</div>
							{#if isDisabled}
								<Tooltip class={twMerge("absolute z-10 max-w-64 border-gray-700 p-0 ")} placement="right">
									{#if title}
										<div class=" relative z-10 rounded-t-md border-b border-gray-700 bg-linear-to-b from-gray-900 to-gray-700 px-4 py-2">
											<Heading tag="h3" class="text-sm font-medium text-gray-300">Why is this disabled for me?</Heading>
										</div>
									{/if}
									<div class="border-b-md px-4 py-2 text-sm font-normal text-gray-300">You don't have file library manager permission. To be able to upload new, reach out to your workspace admin to get access.</div>
								</Tooltip>
							{/if}
						{/each}
					</SidebarGroup>
				</SidebarWrapper>
			</Sidebar>
			<div class="grow overflow-y-visible">
				{#if tab === 0}
					<div class="flex h-full flex-1 flex-col">
						{#if !disableUploadNew}
							<P size="base" color="text-black" class="mb-4 font-medium">Upload a new image from my computer</P>
							<Dropzone on:change={onFileDropped} bind:lastError={modalError} dropzoneClass={twMerge("aspect-[auto]", !fromOrgAdmin ? "py-4" : "grow py-grow h-auto")} fileTypes={fileTypes.join(",")}>
								{#if loading}
									<div class="flex h-full w-full items-center justify-center bg-gray-50">
										<Spinner />
									</div>
								{:else}
									<div class="flex grow flex-col items-center justify-center text-gray-500">
										<Icon name="cloud-arrow-up" class="h-10 w-10" />
										<P size="sm" class="font-medium">Click to upload</P>
										<P size="xs">or drag and drop</P>
									</div>
								{/if}
							</Dropzone>
							<P secondary size="xs" class="mt-2 font-normal">You can upload PNG, JPEG, SVG up to 10 MB. Files uploaded here are hosted on a public URL. Avoid uploading anything confidential.</P>
						{/if}
						{#if !fromOrgAdmin}
							<div class="mt-4 overflow-y-auto">
								<FileExplorer bind:selectedFile={selectedImgFromLibrary} {fileTypes} />
							</div>
						{/if}
					</div>
				{:else if tab === 1}
					<div class="flex h-full flex-col overflow-y-visible">
						<P size="base" color="text-black" class="mb-4 font-medium">Point to web image</P>
						<P secondary size="sm" class="mt-2 mb-6">Note: This is referring to a live web image and will update automatically as the source changes.</P>
						<Label id="edit-image-url-label" for="edit-image-url" defaultClass="font-bold text-sm">Image URL</Label>
						<div class="mb-2"></div>
						{#if $provider}
							<PlaintextEditor provider={$provider.withTokensOfType(DataType.STRING, DataType.URL, DataType.IMAGE)} value={changedValue} aria-labelledby="edit-image-url-label" on:input={handleTokenizedChange} autofocus={true} />
						{:else}
							<Input type="url" value={changedValue} name="edit-image-url" on:change={handleChange} />
						{/if}
					</div>
				{:else if tab === 2}
					<FileExplorer bind:selectedFile={selectedImgFromLibrary} {fileTypes} />
				{/if}
			</div>
		</div>
		<svelte:fragment slot="footer">
			<div class="flex w-full justify-end gap-2">
				<Button data-action-name="file-management-button" data-action-title="Cancel" class="self-end" color="alternative" on:click={handleClose}>Cancel</Button>
				<Button data-action-name="file-management-button" data-action-title="Continue" color="primary" disabled={!(changedValue || hasSelectedFromLibrary)} loading={isFetching} on:click={onContinue}>Continue</Button>
			</div>
		</svelte:fragment>
	</Modal>
{:else}
	<ImagePicker
		open={openCropper}
		on:change
		{imageTypeCaption}
		{contentType}
		{value}
		{focalPoint}
		{aspectRatio}
		{initialAspectRatio}
		{shape}
		{caption}
		{finalWidth}
		{finalHeight}
		{fileTypes}
		on:replace={replace}
		{showFileManagerCheckbox}
		on:close={handleClose}
		{uploadId}
		{imageSize}
		{fileName}
		allowFocalPointSelection={allowFocalPointSelection && tab !== 1}
		{showAspectRatioPicker}
		{preferFreeformAspectRatio}
	/>
{/if}
